import { useEffect, useState } from "react";
import { FiEdit2, FiCalendar, FiChevronDown, FiTrash2 } from "react-icons/fi";
import {
  FaFlask,
  FaPills,
  FaXRay,
  FaPlus,
  FaHeartbeat,
  FaAppleAlt,
  FaTint,
} from "react-icons/fa";
import { BsClipboard2Pulse, BsPersonBadge } from "react-icons/bs";
import IPDOrderService from "../../../../Services/IPDOrderService";
import AddOrderModal from "./AddOrderModal";
import DeleteConfirmModal from "./DeleteConfirmModal";

const TYPE_ICON = {
  MEDICATION: (
    <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
      <FaPills className="text-pink-500 text-[14px]" />
    </div>
  ),
  DRUG: (
    <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
      <FaPills className="text-pink-500 text-[14px]" />
    </div>
  ),
  LAB: (
    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
      <FaFlask className="text-green-600 text-[14px]" />
    </div>
  ),
  DIAGNOSTIC: (
    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
      <FaXRay className="text-blue-500 text-[14px]" />
    </div>
  ),
  Procedure: (
    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
      <FaHeartbeat className="text-red-500 text-[14px]" />
    </div>
  ),
  IPD: (
    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
      <BsPersonBadge className="text-teal-600 text-[14px]" />
    </div>
  ),
  DIET: (
    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
      <FaAppleAlt className="text-amber-500 text-[14px]" />
    </div>
  ),
  Consultation: (
    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
      <BsClipboard2Pulse className="text-orange-500 text-[14px]" />
    </div>
  ),
  "Blood Bank": (
    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
      <FaTint className="text-red-500 text-[14px]" />
    </div>
  ),
};

const getTypeIcon = (type) =>
  TYPE_ICON[type] || (
    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
      <BsClipboard2Pulse className="text-gray-500 text-[14px]" />
    </div>
  );

const STATUS_STYLE = {
  1: "text-orange-500",
  2: "text-emerald-600",
  3: "text-blue-600",
};

const PRIORITY_STYLE = {
  HIGH: "bg-red-100 text-red-600",
  MEDIUM: "bg-orange-100 text-orange-500",
  LOW: "bg-green-100 text-green-600",
  High: "bg-red-100 text-red-600",
  Medium: "bg-orange-100 text-orange-500",
  Low: "bg-green-100 text-green-600",
};

export default function Orders({
  patientDetails,
  patientId,
  admissionId,
  admission,
}) {
  const [showModal, setShowModal] = useState(false);
  const [orderList, SetOrderList] = useState([]);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getOrderList = async () => {
    await IPDOrderService.getIPDOrderDetails({
      patientId: patientDetails.patientId,
      admissionId:admissionId
    })
      .then((res) => {
        console.log("res", res.data);
        SetOrderList(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const effectivePatientId = patientId || patientDetails?.patientId;

  const openDeleteModal = (order) => setOrderToDelete(order);
  const closeDeleteModal = () => {
    if (!isDeleting) setOrderToDelete(null);
  };

  const confirmDelete = async () => {
    if (!orderToDelete) return;
    const payload = {
      patientId: orderToDelete.patientId || effectivePatientId,
      orderCode: orderToDelete.orderId,
    };
    setIsDeleting(true);
    await IPDOrderService.DeletePatientOrder(payload)
      .then(() => {
        setOrderToDelete(null);
        getOrderList();
      })
      .catch((err) => {
        console.error("Delete order failed:", err);
      })
      .finally(() => setIsDeleting(false));
  };

  useEffect(() => {
    getOrderList();
  }, []);
  return (
    <div className="flex flex-col gap-5 py-5">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] font-bold text-[#111827]">Orders </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowModal(true)}
            className="h-10 px-5 rounded-lg bg-[#008f9f] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#007a8a] transition-colors"
          >
            <FaPlus className="text-[12px]" />
            Add Order
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-[#e9edf5] rounded-[18px] overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[#edf1f7]">
              {[
                "Order Type",
                "Priority",
                "Ordered By",
                "Order Date",
                "Status",
                "Actions",
              ].map((col) => (
                <th
                  key={col}
                  className="py-4 px-4 text-[12px] font-bold text-[#111827] whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orderList.map((order) => (
              <tr
                key={order.id}
                className="border-b border-[#edf1f7] last:border-0 hover:bg-[#fafbff] transition-colors"
              >
                {/* Order Type with icon */}
                <td className="py-4 px-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(order.orderType)}
                    <span className="text-[12px] font-medium text-[#374151]">
                      {order.orderType}
                    </span>
                  </div>
                </td>

                {/* Priority */}
                <td className="py-4 px-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1 rounded-md text-[11px] font-bold ${
                      PRIORITY_STYLE[order.priority] ||
                      "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {order.priority}
                  </span>
                </td>

                {/* Ordered By */}
                <td className="py-4 px-4 text-[12px] text-[#374151] whitespace-nowrap">
                  {order.orderedBy}
                </td>

                {/* Order Date */}
                <td className="py-4 px-4 text-[12px] text-[#4b5563] whitespace-nowrap">
                  {order.orderDate}
                </td>

                {/* Status */}
                <td className="py-4 px-4 whitespace-nowrap">
                  <span
                    className={`text-[12px] font-bold ${
                      STATUS_STYLE[order.status] || "text-gray-600"
                    }`}
                  >
                    {order.status == 1
                      ? "Pending"
                      : order.status == 2
                        ? "Completed"
                        : order.status == 3
                          ? "In Progress"
                          : "N/A"}
                  </span>
                </td>

                {/* Actions */}
                <td className="py-4 px-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {/* <button className="w-7 h-7 flex  bg-white items-center justify-center rounded-md border border-[#edf1f7] text-[#5b5bd6] hover:bg-[#eef2ff]">
                      <FiEdit2 className="text-[12px]" />
                    </button> */}
                    <button
                      onClick={() => openDeleteModal(order)}
                      className="w-7 h-7 flex  bg-white items-center justify-center rounded-md border border-[#edf1f7] text-red-400 hover:bg-red-50"
                    >
                      <FiTrash2 className="text-[12px]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Order Modal */}
      {showModal && (
        <AddOrderModal
          onClose={() => setShowModal(false)}
          onSuccess={getOrderList}
          patientDetails={patientDetails}
          patientId={patientId}
          admissionId={admissionId}
          admission={admission}
        />
      )}

      {/* Delete Confirmation Modal */}
      {orderToDelete && (
        <DeleteConfirmModal
          order={orderToDelete}
          isDeleting={isDeleting}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
        />
      )}
    </div>
  );
}
